import React, { useEffect, useRef, useState } from "react";

/**
 * ConfirmDialog
 *
 * Props:
 *  - open: boolean
 *  - title: string
 *  - message: string | React node
 *  - confirmText: string (optional, default "Yes")
 *  - cancelText: string (optional, default "Cancel")
 *  - showInput: boolean (optional) => show a text input for user input
 *  - defaultValue: string (optional) => default value for the input
 *  - onConfirm(value?) => called when user confirms, value is input text if showInput
 *  - onCancel() => called when user cancels/close
 */

export default function ConfirmDialog({
  open,
  title = "",
  message = "",
  confirmText = "Yes",
  cancelText = "Cancel",
  showInput = false,
  defaultValue = "",
  onConfirm,
  onCancel,
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const confirmBtnRef = useRef(null);

  // Reset input value when dialog opens
  useEffect(() => {
    setValue(defaultValue ?? "");
  }, [defaultValue, open]);

  // Focus handling
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      if (showInput && inputRef.current) {
        inputRef.current.focus();
      } else if (confirmBtnRef.current) {
        confirmBtnRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [open, showInput]);

  // Keyboard handling
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onCancel?.();
    } else if (e.key === "Enter") {
      e.preventDefault();
      onConfirm?.(showInput ? value : undefined);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onKeyDown={handleKeyDown}
    >
      <div className="modal-card" style={{ width: 520, maxWidth: "100%" }}>
        {/* Header */}
        <div
          style={{
            marginBottom: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
          <button
            className="modal-close"
            onClick={onCancel}
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        {/* Message */}
        <div style={{ marginBottom: 12, color: "#333" }}>
          {typeof message === "string" ? (
            <div style={{ whiteSpace: "pre-wrap" }}>{message}</div>
          ) : (
            message
          )}
        </div>

        {/* Optional input */}
        {showInput && (
          <div style={{ marginBottom: 12 }}>
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="fld"
              placeholder="Enter text"
            />
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 8,
          }}
        >
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {cancelText}
          </button>

          <button
            type="button"
            ref={confirmBtnRef}
            className="btn-primary"
            onClick={() => onConfirm(showInput ? value : undefined)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
